import React, { useState, useEffect } from "react";
import { Tabs, Tab, CircularProgress } from "@mui/material";
import { HttpService } from "../../service/HttpService";
import dayjs from "dayjs";
import "./SapAnalysis.scss";

const SapAnalysisArchive = ({ roomId }) => {
  const httpService = new HttpService();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (roomId) {
      fetchArchivedReports(roomId);
    }
  }, [roomId]);

  const fetchArchivedReports = async (roomId) => {
    setLoading(true);
    try {
      const res = await httpService.get(
        `/sap-analysis?roomId=${roomId}&is_archived=true`,
      );
      const data = res?.data || res?.data?.data || [];
      // Also it's possible res.data is the array, or res.data.data
      const records = Array.isArray(data) ? data : data.data || [];
      setReports(records);
      setActiveTab(0);
    } catch (error) {
      console.error("Error fetching archived sap analysis reports", error);
      setReports([]);
    }
    setLoading(false);
  };

  const formatGrowthPhase = (name) => {
    if (!name) return "";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0][0].toUpperCase()}${parts[1]}`;
    }
    return name;
  };

  const generateTabLabel = (record) => {
    const cultivarName = record.cultivar_name || "Unknown";
    const phase = formatGrowthPhase(record.growth_phase_name);
    const date = record.test_date
      ? dayjs(record.test_date).format("MM/DD/YYYY")
      : "";
    return `${cultivarName} - ${phase} - ${date}`;
  };

  const tabsSx = {
    "& .MuiTabs-indicator": {
      backgroundColor: "#00A272",
      height: "3px",
    },
    "& .MuiTab-root": {
      position: "relative",
      color: "#A1A2A1",
      fontWeight: 400,
      fontSize: "16px",
      textTransform: "none",
      padding: "12px 20px",
      marginRight: "8px",
      "&:not(.Mui-selected)::after": {
        content: '""',
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: "3px",
        backgroundColor: "#C4C4C4",
      },
      "&.Mui-selected": {
        color: "#00A272 !important",
        fontWeight: 600,
        backgroundColor: "#E6F6F1",
        borderRadius: "4px 4px 0 0",
      },
    },
  };

  if (loading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "50px" }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="sap-analysis-container">
      {reports.length > 0 ? (
        <>
          <div className="tabs-container">
            <Tabs
              value={activeTab}
              onChange={(e, nv) => setActiveTab(nv)}
              variant="scrollable"
              scrollButtons={false}
              sx={tabsSx}
            >
              {reports.map((record, index) => (
                <Tab
                  key={record.id || index}
                  label={generateTabLabel(record)}
                />
              ))}
            </Tabs>
          </div>

          <div className="pdf-viewer-inline">
            {reports[activeTab]?.pdf_url ? (
              <iframe
                // #toolbar=0&navpanes=0
                src={`${reports[activeTab].pdf_url}`}
                title="PDF Viewer"
                style={{ height: "800px", border: "1px solid #ccc" }}
              />
            ) : (
              <div
                style={{ padding: "20px", textAlign: "center", color: "#666" }}
              >
                No PDF available for this record.
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="tabs-container" style={{ padding: "12px 24px" }}>
          <p style={{ color: "#777", margin: 0, fontSize: "14px" }}>
            No archived SAP Analysis reports for this room.
          </p>
        </div>
      )}
    </div>
  );
};

export default SapAnalysisArchive;
